import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, DollarSign, ShoppingCart, 
  ArrowRight, RefreshCw, AlertTriangle, Mail
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FunnelAnalytics() {
  const [stats, setStats] = useState(null);
  const [abandoned, setAbandoned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadStats();
  }, [days]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Load funnel stats
      const statsRes = await fetch(`${API_URL}/api/admin/analytics/funnel?days=${days}`, { headers });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      // Load abandoned checkouts
      const abandonedRes = await fetch(`${API_URL}/api/admin/analytics/abandoned-checkouts?hours=48`, { headers });
      if (abandonedRes.ok) {
        const data = await abandonedRes.json();
        setAbandoned(data.abandoned || []);
      }
    } catch (err) {
      setError('Failed to load funnel analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-12 text-red-600">
        {error || 'Failed to load analytics'}
      </div>
    );
  }

  const funnel = stats.funnel || {};
  const rates = stats.conversion_rates || {};
  const revenue = stats.revenue || {};

  // Calculate total revenue
  const totalRevenue = (
    (revenue.documentation_revenue_cents || 0) + 
    (revenue.assessment_revenue_cents || 0) + 
    (revenue.declaration_revenue_cents || 0)
  ) / 100;

  return (
    <div className="space-y-6" data-testid="funnel-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#102A43]">Conversion Funnel</h2>
            <p className="text-sm text-[#64748B]">Last {days} days</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-200 rounded px-3 py-1 text-sm"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <Button variant="ghost" size="sm" onClick={loadStats}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-emerald-700">Total Revenue</span>
          </div>
          <p className="text-3xl font-bold text-emerald-800">${totalRevenue.toLocaleString()}</p>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Documentation ($97)</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{revenue.documentation_sales || 0}</p>
          <p className="text-xs text-gray-500">${((revenue.documentation_revenue_cents || 0) / 100).toLocaleString()}</p>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-gray-600">Assessment ($297)</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{revenue.assessment_sales || 0}</p>
          <p className="text-xs text-gray-500">${((revenue.assessment_revenue_cents || 0) / 100).toLocaleString()}</p>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-gray-600">Declaration ($997)</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{revenue.declaration_sales || 0}</p>
          <p className="text-xs text-gray-500">${((revenue.declaration_revenue_cents || 0) / 100).toLocaleString()}</p>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-medium text-[#102A43] mb-6">Conversion Funnel</h3>
        
        <div className="space-y-4">
          {/* Content Viewed */}
          <FunnelStep 
            label="Content Viewed"
            count={funnel.content_viewed || 0}
            rate={null}
            color="bg-gray-100"
          />
          <FunnelArrow rate={rates.content_to_assessment} />
          
          {/* Assessment Started */}
          <FunnelStep 
            label="Assessment Started"
            count={funnel.assessment_started || 0}
            rate={rates.content_to_assessment}
            color="bg-blue-100"
          />
          <FunnelArrow rate={rates.assessment_start_to_complete} />
          
          {/* Assessment Completed */}
          <FunnelStep 
            label="Assessment Completed"
            count={funnel.assessment_completed || 0}
            rate={rates.assessment_start_to_complete}
            color="bg-blue-200"
          />
          <FunnelArrow rate={rates.assessment_to_lead} />
          
          {/* Lead Captured */}
          <FunnelStep 
            label="Lead Captured"
            count={funnel.lead_captured || 0}
            rate={rates.assessment_to_lead}
            color="bg-emerald-100"
          />
          <FunnelArrow rate={rates.lead_to_doc_checkout} />
          
          {/* Documentation Checkout */}
          <FunnelStep 
            label="Documentation Checkout Started"
            count={funnel.documentation_checkout_started || 0}
            rate={rates.lead_to_doc_checkout}
            color="bg-amber-100"
          />
          <FunnelArrow rate={rates.doc_checkout_to_purchase} />
          
          {/* Documentation Purchased */}
          <FunnelStep 
            label="Documentation Purchased"
            count={funnel.documentation_purchased || 0}
            rate={rates.doc_checkout_to_purchase}
            color="bg-emerald-200"
          />
        </div>
      </div>

      {/* Axiom & Tools */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-medium text-[#102A43] mb-4">Axiom Engagement</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-700">{stats.axiom?.opened || 0}</p>
              <p className="text-xs text-purple-600">Opened</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-700">{stats.axiom?.messages_sent || 0}</p>
              <p className="text-xs text-purple-600">Messages Sent</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-medium text-[#102A43] mb-4">Free Tools</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{stats.tools?.started || 0}</p>
              <p className="text-xs text-blue-600">Started</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{stats.tools?.completed || 0}</p>
              <p className="text-xs text-blue-600">Completed</p>
              <p className="text-[10px] text-blue-500">{rates.tool_completion_rate}% rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Abandoned Checkouts */}
      {abandoned.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-medium text-amber-800">Abandoned Checkouts (Last 48h)</h3>
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{abandoned.length}</span>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {abandoned.slice(0, 10).map((checkout, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                <div>
                  <p className="text-sm font-medium text-gray-800">{checkout.user_email}</p>
                  <p className="text-xs text-gray-500">
                    {checkout.event_type?.replace('_checkout_started', '').replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(checkout.timestamp).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-3">
            <Mail className="w-3 h-3 inline mr-1" />
            Recovery emails are sent automatically after 2 hours
          </p>
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, count, rate, color }) {
  return (
    <div className={`${color} rounded-lg p-4 flex items-center justify-between`}>
      <span className="font-medium text-[#102A43]">{label}</span>
      <div className="flex items-center gap-4">
        <span className="text-2xl font-bold text-[#102A43]">{count.toLocaleString()}</span>
        {rate !== null && (
          <span className="text-sm text-gray-500">({rate}% from prev)</span>
        )}
      </div>
    </div>
  );
}

function FunnelArrow({ rate }) {
  return (
    <div className="flex items-center justify-center py-1">
      <ArrowRight className="w-5 h-5 text-gray-400 rotate-90" />
      {rate !== undefined && (
        <span className="text-xs text-gray-400 ml-2">{rate}%</span>
      )}
    </div>
  );
}
